import React, { useEffect, useMemo, useState } from "react";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import { GenericModalProps } from "../../utils/constants/generic-types";
import { Stack } from "react-bootstrap";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import WheelSlider from "../WheelSlider/WheelSlider";
import "./DatePicker.scss";

enum DateElements {
  DAY = "day",
  MONTH = "month",
  YEAR = "year",
}

export interface DateValue {
  [DateElements.DAY]: string;
  [DateElements.MONTH]: string;
  [DateElements.YEAR]: string;
}

interface DatePickerProps extends GenericModalProps {
  initialDateValue?: DateValue;
  handleDateSelection: (date: DateValue) => void;
}

const getDays = (monthIndex: number, year: number) => {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
};

// In the computation within the map function, 18 is subtracted from the current year because we only want to show users years that would amount to at least 18.
// Consequently, "NO_OF_YEARS - 1" substitutes array.length - 1 since we can't determine the array length within the map function. This subtraction is done in order to return the years in ascending order.
const NO_OF_YEARS = 77;
const years = Array.from({ length: NO_OF_YEARS }, (_, i) =>
  (new Date().getFullYear() - 18 - (NO_OF_YEARS - 1) + i).toString(),
);

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultDateValue = {
  day: "",
  month: "",
  year: "",
};

export default function DatePicker({
  isOpen,
  onClose,
  initialDateValue,
  handleDateSelection,
}: DatePickerProps) {
  const [selectedDate, setSelectedDate] = useState<DateValue>(
    initialDateValue ?? defaultDateValue,
  );
  const days = useMemo(
    () => getDays(months.indexOf(selectedDate.month), +selectedDate.year),
    [selectedDate.month, selectedDate.year],
  );

  const changeSelectedDate = (
    dateElement: DateElements,
    value: string | number,
  ) => {
    setSelectedDate((prevDate) => ({ ...prevDate, [dateElement]: value }));
  };

  const confirmDateSelection = () => {
    onClose();
    handleDateSelection(selectedDate);
  };

  useEffect(() => {
    initialDateValue && setSelectedDate(initialDateValue);
  }, [initialDateValue]);

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClass="DatePicker"
      backdropClassName="DatePickerBackdrop"
    >
      <Stack gap={4} className="pt-2">
        <p className="text-center Hanson-700 Heading">Select date of birth</p>
        <Stack direction="horizontal" className="MobileOnly DateSliders">
          <div className="WheelShadowTop" />
          <WheelSlider
            values={months}
            horizontalAlignment="start"
            containerClass="MonthsWheel"
            initValueIndex={
              selectedDate.month
                ? months.indexOf(selectedDate.month)
                : undefined
            }
            handleChange={(value) =>
              changeSelectedDate(DateElements.MONTH, value)
            }
          />
          <WheelSlider
            values={days}
            containerClass="DaysWheel"
            initValueIndex={
              selectedDate.day ? days.indexOf(selectedDate.day) : undefined
            }
            handleChange={(value) =>
              changeSelectedDate(DateElements.DAY, value)
            }
          />
          <WheelSlider
            values={years}
            containerClass="YearsWheel"
            initValueIndex={years.indexOf(selectedDate.year || "2000")}
            handleChange={(value) =>
              changeSelectedDate(DateElements.YEAR, value)
            }
          />
          <div className="WheelShadowBottom" />
        </Stack>
        <PrimaryActionButton
          size="lg"
          text="Confirm"
          onClick={confirmDateSelection}
        />
      </Stack>
    </BottomDrawer>
  );
}
